import {useNuxtApp} from "nuxt/app";
import {readonly, ref, type Ref} from "vue";

// Only ever set on the client-side, so sharing this between server-side requests is fine
const hydrated = ref(false);
let hookRegistered = false;

/**
 * Whether the app has finished hydrating the server-rendered HTML. Always `false` on the
 * server-side.
 *
 * Use this to show visitor-specific data (profile, cart, preferences stored in cookies)
 * only after hydration. Server-rendered HTML may be cached and served to other visitors
 * (Cloudflare, nuxt SWR), so it must not contain such data, and rendering it during
 * hydration would cause hydration mismatches.
 *
 * Unlike a component's `onMounted`, this works outside components and flips only once
 * for the whole app.
 */
export function useHydrated(): Readonly<Ref<boolean>> {
	if (import.meta.client && !hookRegistered) {
		hookRegistered = true;

		const nuxtApp = useNuxtApp();

		if (nuxtApp.isHydrating) {
			nuxtApp.hooks.hookOnce("app:suspense:resolve", () => {
				hydrated.value = true;
			});
		} else {
			hydrated.value = true;
		}
	}

	return readonly(hydrated);
}
